// The suite's mode switcher (docs/body/architecture.md §2): a small menu beside a mode's brand that lists the modes
// in table order, with the current one marked, and links to the mode's gallery page, the Labs list and the parent
// site. A switch is an ordinary link to the other mode's path (one history entry; the developer switches kept), so
// each mode stays its own page with its own store. Nothing here runs at import time. Every word here is approved
// public copy (docs/body/landing-copy.md); a mode's one-line description and status words join once approved.
import * as Popover from '@radix-ui/react-popover';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { LABS_HREF, LABS_OPEN, listedModes, modeById, modeHref, SITE_URL, STATUS_WORD, type ModeId } from './modes.ts';
import './suite.css';

export function ModeSwitcher({ mode, search }: { mode: ModeId; search?: string }) {
  const [open, setOpen] = useState(false);
  const current = modeById(mode);
  // The address is read when the menu renders, never when the module loads (a lab page evaluates it too).
  const q = search ?? (typeof location === 'undefined' ? '' : location.search);
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button type="button" className="mode-switch" aria-label={`Modes: ${current.name} is open`} data-mode={mode}>
          <ChevronDown size={16} aria-hidden="true" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        {/* Escape closes the menu here; stopped so the app's shortcuts don't also act on it. */}
        <Popover.Content className="mode-menu" side="bottom" align="start" sideOffset={8} collisionPadding={12} onEscapeKeyDown={(e) => e.stopPropagation()}>
          <p className="mode-menu-title ba-type-overline">Neurosimulator</p>
          <nav aria-label="Modes">
            {listedModes(mode).map((m) => {
              const on = m.id === mode;
              return (
                <a key={m.id} className={`mode-item ${on ? 'on' : ''}`} href={on ? undefined : modeHref(m, q)} aria-current={on ? 'page' : undefined} data-mode={m.id} data-status={m.status} onClick={() => setOpen(false)}>
                  <span className="mode-item-name ba-type-body-strong">
                    {m.name}
                    {m.status !== 'live' ? <span className="mode-status">{STATUS_WORD}</span> : null}
                  </span>
                </a>
              );
            })}
          </nav>
          <div className="mode-links ba-type-small">
            {/* The two links that leave the app open in a new tab, so a scene someone set up isn't lost to a menu click. */}
            {current.gallery ? <a className="mode-link" href={current.gallery} data-link="gallery" target="_blank" rel="noopener">The {current.name.toLowerCase()} gallery</a> : null}
            {LABS_OPEN ? <a className="mode-link" href={LABS_HREF} data-link="labs">Labs</a> : null}
            <a className="mode-link" href={SITE_URL} data-link="site" target="_blank" rel="noopener">neurosimulator.ai</a>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
